import { createClient, type SanityClient } from "@sanity/client";
import {
  DEFAULT_ABOUT_PAGE,
  DEFAULT_LANDING_PAGE,
  resolveMarketingTheme,
  type AboutPageData,
  type LandingPageData,
  type SanityImageRef,
} from "@/lib/marketingDefaults";

export const landingPageQuery = `*[_type == "landingPage"][0]{
  themePreset,
  seo,
  brand{ logo },
  nav,
  footer,
  waitlist,
  hero{
    ...,
    visualBackground
  },
  nightSection,
  featuresSection,
  venuesSection,
  outlookSection,
  timelineSteps,
  featureCards,
  featureMockups,
  venueCategories,
  outlookCards,
  finalCta
}`;

export const aboutPageQuery = `*[_type == "aboutPage"][0]{
  themePreset,
  seo,
  brand{ logo },
  nav,
  footer,
  manifesto{ ..., logo },
  comparison,
  vision,
  focus{ ..., mark },
  community
}`;

function projectId(): string {
  return (import.meta.env.VITE_SANITY_PROJECT_ID ?? "").trim();
}

function dataset(): string {
  return (import.meta.env.VITE_SANITY_DATASET ?? "production").trim() || "production";
}

export function isSanityConfigured(): boolean {
  return Boolean(projectId());
}

/** Marketing fetches always hit the API (not CDN) so Studio publishes show up on soft reloads. */
export function createSanityClient(opts?: { preview?: boolean }): SanityClient | null {
  const id = projectId();
  if (!id) return null;
  const preview = Boolean(opts?.preview);
  const token = (import.meta.env.VITE_SANITY_API_TOKEN ?? "").trim();
  return createClient({
    projectId: id,
    dataset: dataset(),
    apiVersion: "2025-01-01",
    useCdn: false,
    perspective: preview ? "previewDrafts" : "published",
    token: preview || token ? token || undefined : undefined,
    stega: preview ? { enabled: true, studioUrl: "https://pulse-marketing.sanity.studio" } : undefined,
  });
}

export function isSanityPreviewRequest(search: Record<string, unknown>): boolean {
  const flag = String(search["sanity-preview"] ?? "").toLowerCase();
  if (flag !== "true" && flag !== "1") return false;
  const secret = (import.meta.env.VITE_SANITY_PREVIEW_SECRET ?? "").trim();
  if (!secret) return false;
  return String(search["sanity-preview-secret"] ?? "") === secret;
}

function imageUrl(_client: SanityClient, image: SanityImageRef, width: number): string | undefined {
  if (!image?.asset) return undefined;
  if (typeof image.asset.url === "string" && image.asset.url) {
    return `${image.asset.url}?w=${width}&auto=format`;
  }
  const ref = image.asset._ref ?? image.asset._id;
  if (!ref) return undefined;
  const match = /^image-([a-f0-9]+)-(\d+x\d+)-(\w+)$/.exec(ref);
  if (!match) return undefined;
  const [, id, dims, format] = match;
  return `https://cdn.sanity.io/images/${projectId()}/${dataset()}/${id}-${dims}.${format}?w=${width}&auto=format`;
}

function deepMergeNav(
  base: LandingPageData["nav"],
  patch: Partial<LandingPageData["nav"]> | null | undefined,
): LandingPageData["nav"] {
  return {
    ...base,
    ...patch,
    homeLinks: { ...base.homeLinks, ...patch?.homeLinks },
    aboutLinks: { ...base.aboutLinks, ...patch?.aboutLinks },
  };
}

export async function fetchLandingPage(preview = false): Promise<LandingPageData> {
  const client = createSanityClient({ preview });
  if (!client) return DEFAULT_LANDING_PAGE;
  try {
    const data = await client.fetch<Partial<LandingPageData> | null>(landingPageQuery);
    if (!data?.hero?.headline) return DEFAULT_LANDING_PAGE;
    const merged: LandingPageData = {
      ...DEFAULT_LANDING_PAGE,
      ...data,
      themePreset: resolveMarketingTheme(data.themePreset ?? DEFAULT_LANDING_PAGE.themePreset),
      seo: { ...DEFAULT_LANDING_PAGE.seo, ...data.seo },
      brand: { ...DEFAULT_LANDING_PAGE.brand, ...data.brand },
      nav: deepMergeNav(DEFAULT_LANDING_PAGE.nav, data.nav),
      footer: { ...DEFAULT_LANDING_PAGE.footer, ...data.footer },
      waitlist: { ...DEFAULT_LANDING_PAGE.waitlist, ...data.waitlist },
      hero: { ...DEFAULT_LANDING_PAGE.hero, ...data.hero },
      nightSection: { ...DEFAULT_LANDING_PAGE.nightSection, ...data.nightSection },
      featuresSection: { ...DEFAULT_LANDING_PAGE.featuresSection, ...data.featuresSection },
      venuesSection: { ...DEFAULT_LANDING_PAGE.venuesSection, ...data.venuesSection },
      outlookSection: { ...DEFAULT_LANDING_PAGE.outlookSection, ...data.outlookSection },
      featureMockups: {
        ...DEFAULT_LANDING_PAGE.featureMockups,
        ...data.featureMockups,
        chatChips:
          data.featureMockups?.chatChips?.length
            ? data.featureMockups.chatChips
            : DEFAULT_LANDING_PAGE.featureMockups.chatChips,
        listRows:
          data.featureMockups?.listRows?.length
            ? data.featureMockups.listRows
            : DEFAULT_LANDING_PAGE.featureMockups.listRows,
      },
      timelineSteps: data.timelineSteps?.length
        ? data.timelineSteps
        : DEFAULT_LANDING_PAGE.timelineSteps,
      featureCards: data.featureCards?.length
        ? data.featureCards
        : DEFAULT_LANDING_PAGE.featureCards,
      venueCategories: data.venueCategories?.length
        ? data.venueCategories
        : DEFAULT_LANDING_PAGE.venueCategories,
      outlookCards: data.outlookCards?.length
        ? data.outlookCards
        : DEFAULT_LANDING_PAGE.outlookCards,
      finalCta: { ...DEFAULT_LANDING_PAGE.finalCta, ...data.finalCta },
    };
    merged.brand.logoUrl =
      imageUrl(client, merged.brand.logo, 128) ?? merged.brand.logoUrl;
    merged.hero.visualBackgroundUrl =
      imageUrl(client, merged.hero.visualBackground, 1200) ?? merged.hero.visualBackgroundUrl;
    return merged;
  } catch {
    return DEFAULT_LANDING_PAGE;
  }
}

export async function fetchAboutPage(preview = false): Promise<AboutPageData> {
  const client = createSanityClient({ preview });
  if (!client) return DEFAULT_ABOUT_PAGE;
  try {
    const data = await client.fetch<Partial<AboutPageData> | null>(aboutPageQuery);
    if (!data?.manifesto?.headlineLine1) return DEFAULT_ABOUT_PAGE;
    const merged: AboutPageData = {
      ...DEFAULT_ABOUT_PAGE,
      ...data,
      themePreset: resolveMarketingTheme(data.themePreset ?? DEFAULT_ABOUT_PAGE.themePreset),
      seo: { ...DEFAULT_ABOUT_PAGE.seo, ...data.seo },
      brand: { ...DEFAULT_ABOUT_PAGE.brand, ...data.brand },
      nav: deepMergeNav(DEFAULT_ABOUT_PAGE.nav, data.nav),
      footer: { ...DEFAULT_ABOUT_PAGE.footer, ...data.footer },
      manifesto: { ...DEFAULT_ABOUT_PAGE.manifesto, ...data.manifesto },
      comparison: {
        ...DEFAULT_ABOUT_PAGE.comparison,
        ...data.comparison,
        oldTags: data.comparison?.oldTags?.length
          ? data.comparison.oldTags
          : DEFAULT_ABOUT_PAGE.comparison.oldTags,
      },
      vision: {
        ...DEFAULT_ABOUT_PAGE.vision,
        ...data.vision,
        paragraphs: data.vision?.paragraphs?.length
          ? data.vision.paragraphs
          : DEFAULT_ABOUT_PAGE.vision.paragraphs,
        expansionStages: data.vision?.expansionStages?.length
          ? data.vision.expansionStages
          : DEFAULT_ABOUT_PAGE.vision.expansionStages,
      },
      focus: { ...DEFAULT_ABOUT_PAGE.focus, ...data.focus },
      community: { ...DEFAULT_ABOUT_PAGE.community, ...data.community },
    };
    merged.brand.logoUrl =
      imageUrl(client, merged.brand.logo, 128) ?? merged.brand.logoUrl;
    merged.manifesto.logoUrl =
      imageUrl(client, merged.manifesto.logo, 200) ??
      merged.brand.logoUrl ??
      merged.manifesto.logoUrl;
    merged.focus.markUrl =
      imageUrl(client, merged.focus.mark, 96) ??
      merged.brand.logoUrl ??
      merged.focus.markUrl;
    return merged;
  } catch {
    return DEFAULT_ABOUT_PAGE;
  }
}
